import React, { createContext, useCallback, useContext, useState } from 'react';
import useSalaWebRTC from '../hooks/useSalaWebRTC';
import SalaAudioSink from '../components/SalaAudioSink';
import { AUDIO_PADRAO, gravarAjustesAudio, lerAjustesAudio } from '../helpers/salaAudio';

// Contexto da sala de voz/video. O Provider vive ACIMA do router (ver App.js)
// para que trocar de aba dentro do portal (ex.: /ti/sala -> /ti/dashboard)
// nao desmonte o hook e derrube a chamada. Enquanto o SalaProvider estiver
// montado, o WebSocket e as RTCPeerConnection continuam vivas.
//
// Como o WebRTC sobrevive mas Sala.js desmonta ao trocar de rota, a
// reproducao de audio TAMBEM tem que morar aqui — do contrario os <video>
// dos tiles somem e o usuario para de ouvir os outros. E isso que o
// SalaAudioSink resolve: fica sempre montado, com um <audio> oculto por
// participante, e so desmuta quando a rota atual NAO for /ti/sala (na propria
// sala o <video> do tile ja toca o audio; desmutar dos dois lados geraria eco).
const SalaContext = createContext(null);

export function SalaProvider({ children }) {
  // `limiteVideoAlta` vem de /sala/salas, mas a busca acontece em Sala.js
  // (e o payload nao interessa a mais ninguem). O Provider so guarda o valor
  // atual e expoe um setter — Sala.js sincroniza quando a presenca chega.
  const [limiteVideoAlta, setLimiteVideoAlta] = useState(3);
  const sala = useSalaWebRTC({ limiteVideoAlta });

  // Ajustes de volume/gate por USUARIO (nao por peerId — ver helpers/salaAudio).
  // Hidratados do localStorage no mount e persistidos em cada mudanca. Vivem
  // aqui (e nao mais em Sala.js) para que o SalaAudioSink continue aplicando
  // volume quando o usuario esta fora de /ti/sala.
  const [audioAjustes, setAudioAjustes] = useState(lerAjustesAudio);

  const atualizarAudioParticipante = useCallback((usuarioId, patch) => {
    if (!usuarioId) return;
    setAudioAjustes((prev) => {
      const proximo = {
        ...prev,
        [usuarioId]: { ...AUDIO_PADRAO, ...(prev[usuarioId] || {}), ...patch },
      };
      gravarAjustesAudio(proximo);
      return proximo;
    });
  }, []);

  const valor = {
    ...sala,
    setLimiteVideoAlta,
    audioAjustes,
    atualizarAudioParticipante,
  };

  return (
    <SalaContext.Provider value={valor}>
      {children}
      {/* Sink recebe tudo por prop para evitar dependencia circular com este
          arquivo (que ja importa SalaAudioSink). */}
      <SalaAudioSink
        participantes={sala.participantes}
        saidaAudio={sala.saidaAudio}
        audioAjustes={audioAjustes}
      />
    </SalaContext.Provider>
  );
}

export function useSala() {
  const ctx = useContext(SalaContext);
  if (!ctx) throw new Error('useSala precisa estar dentro de <SalaProvider>');
  return ctx;
}
